/**
 * Beta waitlist signup client — POST /v1/beta/waitlist (see ato-api's
 * routes/beta_waitlist.ts). Cookie-authenticated (`credentials: "include"`)
 * so a signed-in submission is associated server-side, same convention as
 * vouchClient.ts. The endpoint always answers the same shape whether the
 * email is new or a resubmission — this client has no "already joined"
 * concept of its own, that state lives only in localStorage (see
 * WaitlistModal).
 */

import { accountApiBase, fetchWithConnectionAuth } from "../app/connection";

function apiBase(): string {
  return accountApiBase();
}

export class WaitlistRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = "WaitlistRequestError";
  }
}

export type WaitlistJoinInput = {
  email: string;
  name?: string;
  company?: string;
  useCase?: string;
  product: "cloud" | "desktop";
  source: "pwa_home" | "pwa_desktop" | "ato_web";
  turnstileToken?: string;
};

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

export async function joinWaitlist(input: WaitlistJoinInput): Promise<void> {
  let response: Response;
  try {
    response = await fetchWithConnectionAuth(`${apiBase()}/v1/beta/waitlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        email: input.email,
        name: input.name || undefined,
        company: input.company || undefined,
        use_case: input.useCase || undefined,
        product: input.product,
        source: input.source,
        turnstile_token: input.turnstileToken,
      }),
    });
  } catch (error) {
    throw new WaitlistRequestError(
      error instanceof Error ? error.message : "Network request failed.",
      0,
      "network_error",
    );
  }
  const payload = await readJson(response);
  if (!response.ok) {
    const body = payload as { error?: string; message?: string } | null;
    throw new WaitlistRequestError(
      body?.message || `Request failed (${response.status}).`,
      response.status,
      body?.error || "request_failed",
    );
  }
}
